import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import RichTextEditor from "./RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileText, Printer, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sediList, printLetterHtml } from "../lib/letterPdf";


export default function DocumentoDialog({ open, onOpenChange, pratica, doc }) {
  const [nome, setNome] = useState("");
  const [content, setContent] = useState("");
  const [withHead, setWithHead] = useState("con");
  const [studio, setStudio] = useState(null);
  const [saving, setSaving] = useState(false);
  const inte = useMemo(() => (studio ? sediList(studio)[0] : null), [studio]);

  useEffect(() => {
    if (open && doc) {
      setNome(`${doc.title} — ${pratica?.numero_pratica || ""}`.trim());
      setContent(doc.content || "");
      setWithHead("con");
      api.get("/studio/me").then((r) => setStudio(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc]);

  const save = async () => {
    if (!nome.trim()) { toast.error("Inserisci un nome file"); return; }
    setSaving(true);
    try {
      await api.post(`/studio/pratiche/${pratica.id}/lettere`, { nome: nome.trim(), content });
      toast.success("Documento salvato nella pratica");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-4xl h-[88vh] overflow-hidden flex flex-col" data-testid="documento-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-400" /> {doc?.title || "Documento"}
            <span className="text-sm font-normal text-slate-500">· {pratica?.numero_pratica} {pratica?.nome_assistito}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3 mb-2">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-slate-300 text-xs">Nome file</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} data-testid="documento-nome" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
          </div>
          <Select value={withHead} onValueChange={setWithHead}>
            <SelectTrigger data-testid="documento-letterhead" className="w-48 h-9 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
              <SelectItem value="con">Con carta intestata</SelectItem>
              <SelectItem value="senza">Senza carta intestata</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => printLetterHtml(nome, content, withHead === "con", inte, studio?.settings?.print_margins)} data-testid="documento-print" className="h-9 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Printer className="h-4 w-4 mr-1" /> Stampa
          </Button>
          <Button onClick={save} disabled={saving} data-testid="documento-save" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva nella pratica</>}
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <RichTextEditor value={content} onChange={setContent} withHead={withHead === "con"} letterhead={inte} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
